import humanize from "lodash-es/startCase";
import upperFirst from "lodash-es/upperFirst";

import { toHex } from "common/colors";
import { accounting, sumProperties } from "common/utils";
import Radio from "backbone.radio";
import { View } from "extensions/marionette";
import { CollectionView } from "backbone.marionette";

import { _ } from "extensions/underscore";

import strings from "common/strings";

import { PieChart } from "common/components/charts/pie";
import { references } from "common/helpers/references";

import legendItem from "./pieLegendItem.jst";

const colorsArr = [
    toHex("corp-secondary"), // 0
    toHex("blue-light"), // 1
    toHex("blue-dark"), // 2
    toHex("orange-mid"), // 3
    toHex("gray-dark"), // 4
    toHex("yellow"), // 5
    toHex("purple-mid"), // 6
    toHex("gray-soft"), // 7
    toHex("green-mid"), // 8
    toHex("blue-mid"), // 9
    toHex("aqua") // 10
];

const LegendItemView = View.extend({
    tagName: "li",
    className: "row",
    template: legendItem,
    events: {
        "click a": function(event) {
            this.triggerMethod("drilldown", this, this.model.get(this.getOption("key")), event);
            event.preventDefault();
        },
        "mouseenter a": "setPointActive",
        "mouseleave a": "setPointInactive"
    },
    setPointActive() {
        const modelIndex = this.getViewModelIndexInCollection();
        this.trigger("setChartPointActive", this, modelIndex);
    },
    setPointInactive() {
        const modelIndex = this.getViewModelIndexInCollection();
        this.trigger("setChartPointInactive", this, modelIndex);
    },
    
    getPercentageRelativeToEntireCollection() {
        const pointValueKey = this.getOption("pointValueKey");
        const collectionTotal = this.model.collection.reduce((memo, model) => memo + model.get(pointValueKey), 0);
        const thisModelTotal = this.model.get(pointValueKey);
        
        return accounting.getRate({
            value: thisModelTotal,
            total: collectionTotal,
            precision: 0,
            humanize: true,
            addFloatValue: false
        });
    },
    /**
     * Get Model's index in it's collection
     * @returns {Number}
     */
    getViewModelIndexInCollection() {
        return this.model.collection.indexOf(this.model);
    },
    serializeData() {
        const index = this.getViewModelIndexInCollection();
        const value = this.model.get(this.getOption("pointValueKey"));
        const key = this.getOption("key");
        const formatValue = function(pointType) {
            if (pointType === "count" || pointType === void 0) {
                return this.formatNumber(value);
            }
            
            return this.formatAmountWithCurrentCurrency(value);
        };
    
        return {
            title: upperFirst(references.toAlias(this.model.get(key), key)),
            value: formatValue.call(this, this.getOption("pointValueType")),
            color: colorsArr[index % colorsArr.length], // Cycle thru array values
            percentage: this.getPercentageRelativeToEntireCollection()
        };
    }
});
const LegendCollectionView = CollectionView.extend({
    tagName: "ul",
    childView: LegendItemView,
    childViewEvents: {
        setChartPointActive(view, index) {
            this.trigger("chartPoint", index, "hover");
        },
        setChartPointInactive(view, index) {
            this.trigger("chartPoint", index, null);
        },
        drilldown(...args) {
            this.trigger("drilldown", ...args);
        }
    }
});

export const pieWidgetMixin = {
    addLegend() {
        const legendView = new LegendCollectionView({
            collection: this.groupedCollection,
            childViewOptions: {
                key: this.getDataKey(),
                pointValueKey: this.getPointValueKey(),
                pointValueType: this.getPointValueType(),
                widgetSubtype: this.model.get("widgetSubtype")
            }
        });
        this.listenTo(legendView, "chartPoint", function(index, status) {
            const chartView = this.getChildView("chart");
            if (chartView && chartView.loaded) {
                const _chart = chartView.chart;
                
                // Return earlier if chart still animating
                if (!chartView.loaded) {
                    return;
                }
                if (_chart.series.length) {
                    _chart.series[0].data[index].select();
                }
                if (status === "hover") {
                    _chart.tooltip.refresh(_chart.series[0].data[index]);
                } else {
                    _chart.tooltip.hide();
                }
            }
        });
        this.showChildView("legend", legendView);
    },
    
    getLastPivot() {
        let pivot = this.model.get("options").pivot;
        
        if (!_.isArray(pivot)) {
            pivot = [pivot];
        }
        
        return _.last(pivot);
    },
    
    getDataKey() {
        return Radio.channel("main").request("get:getters").findPivotKey(this.getLastPivot());
    },
    
    getAllLabel() {
        return humanize(`${strings.all} ${Radio.channel("main").request("get:getters").findPivotName(this.getLastPivot(), { plural: true })}`);
    },
    
    getPointValueKey() {
        return this.model.subtypeModel.getClone("pie").pointValue;
    },
    
    getPointValueType() {
        return this.model.subtypeModel.getClone("pie").pointType;
    },
    
    drillDownFromPie({ event, actor, widget }) {
        return widget.drilldownToCorrespondingPage(actor);
    },
    
    valuePostProcessing(val) {
        if (this.model.subtypeModel.getClone("pie").absolute === true) {
            return Math.abs(val);
        }
        return val;
    },
    
    addChart({ widget }) {
        const self = this;
        const key = this.getDataKey();
        const pointValueKey = this.getPointValueKey();
        
        const chartView = new PieChart({
            collection: this.groupedCollection,
            processData(collection) {
                return collection.toJSON();
            },
            valueType: this.model.get("options").by || this.model.subtypeModel.getClone("pie").pointType,
            fullWidth: true,
            chartOptions: {
                chart: {
                    height: this.getChartHeight()
                },
                tooltip: {
                    followPointer: false
                },
                plotOptions: {
                    series: {
                        cursor: "pointer",
                        point: {
                            events: {
                                click(event) {
                                    self.drillDownFromPie({
                                        event,
                                        widget,
                                        actor: this.actor
                                    });
                                }
                            }
                        }
                    }
                }
            },
            series: [{
                type: "pie",
                pointFormat: {
                    y: pointValueKey,
                    name: (point) => references.toAlias(point[key], key),
                    actor: (point) => point[key]
                },
                sorted: true,
                object: true,
                extend: {
                    animation: {
                        complete() {
                            // TODO: check for a memory leak here
                            chartView.loaded = true;
                        }
                    },
                    colors: colorsArr,
                    size: "100%",
                    innerSize: "40%"
                }
            }]
        });
        
        this.showChildView("chart", chartView);
    },
    getEntriesLimit() {
        const isSideBySide = this.model.get("w") >= 6;
        const ENTREE_HEIGHT = isSideBySide ? 51 : 36; // 51 is: 44px height + 1px top-border + 1px bottom border + 5px padding
        const spaceAboveLegend = isSideBySide ? 10 : this.getChartHeight(); // 10 is top padding
        const widgetHeight = this.$el.outerHeight(true) - this.$el.find(".header").outerHeight(true);
        
        return Math.max(0, Math.floor((widgetHeight - spaceAboveLegend) / ENTREE_HEIGHT) - 1); // 1 is left for "Others"
    },
    setGroupedCollection() {
        this.groupedCollection = this.collection.proxy({
            processData: (collection) => {
                if (!collection.length) {
                    return [];
                }
    
                const pointValueKey = this.getPointValueKey();
                const key = this.getDataKey();
                const transformedData = collection.groupReduce({
                    groupBy: key,
                    reduceBy: this.model.subtypeModel.getClone("totals")
                }).map((p) => Object.assign(p, {
                    [pointValueKey]: this.valuePostProcessing(p[pointValueKey])
                }));
                
                const sorted = _.sortBy(transformedData, (point) => -point[pointValueKey]);
    
                let LIMIT_CARD_ENTRIES = this.getEntriesLimit();
                // If Limit is only smaller than actual collection length by one, there is no reason to create "Other" only for one item
                if (sorted.length === LIMIT_CARD_ENTRIES + 1) {
                    LIMIT_CARD_ENTRIES = LIMIT_CARD_ENTRIES + 1;
                }
                
                // Need to get maximum number of entries and put the rest under others
                const topN = _.first(sorted, LIMIT_CARD_ENTRIES) || []; // Return the first n elements of the array.
                const rest = _.rest(sorted, LIMIT_CARD_ENTRIES) || []; // Return the values of the result array from that index onward
                
                // Make a special others object to get the values from `rest` pie chart piece
                if (rest.length) {
                    // Recursively reduce both direct and nested numeric values from the rest of the array
                    const othersReduced = rest.reduce(sumProperties, {});
                    
                    // Don't set "Other" label if other is the only option
                    othersReduced[key] = topN.length ? strings.other : this.getAllLabel();
                    
                    topN.push(othersReduced);
                }
                
                return topN;
            }
        });
        
        return this;
    },
    
    unsetGroupedCollection() {
        // Clean up grouped collection
        if (this.groupedCollection) {
            this.groupedCollection.stopListening();
            this.groupedCollection = null;
        }
    }
};
